/**
 * Items registry — single source of truth.
 *
 * Lives in `shared/` so both server (gameplay, equip checks, recipe dispatch,
 * stat aggregation) and client (tooltip render, inventory UI, shop menus) read
 * the same row. Adding a new item = NOT editing this file — instead, edit the
 * source JSON (`items-data.json`, mirrored from
 * `docs/superpowers/research/2026-04-29-kts-items-full-extract.json`) and let
 * the loader rebuild the registry at module load. This is a synthesizer-driven
 * port, not a hand-written registry, because 221 hand-written rows would be
 * unreadable and lossy.
 *
 * Source: synthesized 2026-04-29 from KTS XML + MapScript by 4 parallel
 * extraction agents + 1 conflict resolver. 221 items, 0 conflicts. Sources
 * audited per item from ItemData.xml / BehaviorData.xml / MapScript.galaxy
 * (preserved in the JSON as `_sources`; dropped from the runtime ItemDef as
 * an extraction artifact).
 *
 * Type adaptations vs the plan v2 sketch (the plan was drafted before the
 * extraction landed; the JSON shape is canonical):
 *   1. `ItemSlot` is 5 values only — "main-hand" | "offhand" | "armor" |
 *      "consumable" | "utility". No "pet" or "candle" — extraction has 0 of
 *      either; D2 cut the candle slot.
 *   2. `ItemSource` is 15 values to cover the full extraction surface
 *      (Smithy, Workbench, HuntersLodge, Tavern, Cooking, Forge, Drop, Quest,
 *      Boss, Starter, Misc, Hidden, RawResource, Tier-Upgrade, Debug).
 *   3. `ItemStats` includes ALL KTS sub-stats + parent attrs + derived +
 *      bonus fields. All optional (nullable in JSON; nulls are stripped on
 *      load so consumers can do `def.stats.physique ?? 0`).
 *   4. `warmthDrainMult`, `miningSpeedMult`, `forestrySpeedMult`,
 *      `petCapDelta` are top-level on `ItemDef` (not under `ItemStats`),
 *      mirroring the JSON.
 *   5. `craftingCost` is `Record<string, number>`. Keys mix base-resource
 *      names (lumber, stone, ironOre, wool, leather, …) with PascalCase item
 *      ids (47 craftables use another item as ingredient — KTS upgrade-chain
 *      pattern, e.g., BeastmastersVambrace requires CrowKeepersBracers).
 *      Recipe dispatch in Task 1.5 resolves which keys are items vs raw
 *      resources via `ITEMS[key] !== undefined` lookup at runtime.
 *   6. `pendingEffects: ReadonlyArray<string>` — entries are PRIMITIVE NAMES
 *      (e.g., "summon", "cooking-buff", "dot-tick"). Distinct primitives in
 *      extraction: summon, cooking-buff, aura-on-hit, spell-cast-trigger,
 *      life-drain, warmth, dot-tick, dash-charge, mana-burn, cc-control,
 *      stealth.
 *   7. `triggeredEffects` is empty for all 221 rows in v1
 *      (extraction's withTriggeredEffects: 0). Phase 3.5 wires effect
 *      dispatch.
 *   8. `levelReq` is normalized to `number` (default 0). JSON sentinels
 *      "1000" and "4294967295" (UINT_MAX) collapse to 999 — per D14, levelReq
 *      is NOT enforced at equip time anyway, so the value is metadata for
 *      bot upgrade-scoring only.
 *   9. `tooltip` keeps KTS markup verbatim (`<n/>`, `<c val="...">`,
 *      `<d ref="...">`) — clients strip/render later.
 *   10. JSON-only artifacts dropped from ItemDef: xmlClass, parent,
 *       classFlags, description, behaviorTooltip, extraStats,
 *       cmdName, cmdIdx, rawIngredients, _sources, sourceAuditReasoning,
 *       altRecipes. (`behaviorIds` is preserved on ItemDef as of Phase 3 —
 *       the equip pipeline feeds it into `getActiveBehaviors` so derived-stats
 *       branches like Bloodcleaver / RockbiterPickaxe fire on equipped items.)
 *
 * Why `ItemId = string` (not a literal union): the registry is built at
 * module load from a JSON-shaped record, so `keyof typeof ITEMS` would not
 * yield a tight literal. Runtime validation by `ITEMS[id] !== undefined` is
 * fine for v1; if Task 1.5 (recipes) or Phase 2 (stat aggregation) needs
 * stricter typing we revisit.
 *
 * See `docs/build/data-driven-features.md` for the single-source-of-truth
 * pattern.
 */
import rawJson from "./items-data.json" with { type: "json" };
import { getItemEffects, type ItemEffect } from "./item-effects-data";

export type ItemSlot =
  | "main-hand"
  | "offhand"
  | "armor"
  | "consumable"
  | "utility";

export type ItemSource =
  | "Smithy"
  | "Workbench"
  | "HuntersLodge"
  | "Tavern"
  | "Cooking"
  | "Forge"
  | "Drop"
  | "Quest"
  | "Boss"
  | "Starter"
  | "Misc"
  | "Hidden"
  | "RawResource"
  | "Tier-Upgrade"
  | "Debug";

/** Item-id alias. Runtime-validated by `ITEMS[id] !== undefined`; not a
 *  closed literal union because the registry is built from JSON at load. */
export type ItemId = string;

/** Crafting cost. Keys are either base-resource names (lumber, stone,
 *  ironOre, wool, leather, raw-meat ids, etc.) OR PascalCase item ids for
 *  the 47 KTS upgrade-chain craftables (e.g. BeastmastersVambrace uses
 *  CrowKeepersBracers as an ingredient). Recipe dispatch resolves which is
 *  which via `ITEMS[key]` lookup. */
export type ResourceCost = Record<string, number>;

/** All KTS stat fields an item may grant. Every field optional — items
 *  granting `physique` only also have a row here with all other fields
 *  undefined. Phase 2.5 derives parent-attr / sub-stat aggregation. */
export interface ItemStats {
  // Sub-stats
  strength?: number;
  dexterity?: number;
  endurance?: number;
  intelligence?: number;
  willpower?: number;
  /** KTS items grant `Focus` as a sub-stat distinct from Dexterity in some
   *  entries, even though Cube is renaming focus->dexterity in Phase 2.
   *  Preserve both fields here so the JSON shape is round-trippable. */
  focus?: number;
  // Parent attributes
  physique?: number;
  wit?: number;
  // Derived
  armor?: number;
  sArmor?: number;
  armorPen?: number;
  manaMax?: number;
  manaRegen?: number;
  hpRegen?: number;
  hpMax?: number;
  critChance?: number;
  critDamage?: number;
  // Bonuses vs. target categories
  attrBonusAnimal?: number;
  attrBonusTree?: number;
  attrBonusStructure?: number;
  attrBonusHero?: number;
  // Combat shape
  attackRange?: number;
  attackPeriod?: number;
  damagePoint?: number;
  moveSpeedMultiplier?: number;
}

/** Cooking-buff metadata for consumable items.
 *
 * Populated by `reference/scripts/extract_cooking_buffs.py` from
 * `MapScript.galaxy:gf_EatFood` (the dispatch where each food item is
 * consumed) and `EffectData.xml` (where `CheffingStats` / `StagFoodStats`
 * etc. hold the actual numeric values referenced via
 * `CatalogFieldValueGet`). Cooking buffs do not live in EffectData chains
 * the way item-equip behaviors do — the galaxy script applies hunger /
 * life / warmth / behavior side-effects directly. This metadata lets the
 * Phase 3.5 cooking-buff effect handler (Task 15) replay those side-effects
 * without hand-coding a switch statement per food.
 *
 * Optional gating like Beer's `lv_warmth += lv_beer/2` (perk-gated on
 * InnerWarmth) is captured as the maximum effect — runtime can apply
 * partial values per perks if desired.
 */
export interface CookingBuffMeta {
  /** Hunger restored, 0..100. From `lv_hunger += X` in galaxy. */
  restoreHunger?: number;
  /** Life (HP) restored. From `gf_HealUnit` arg in galaxy. */
  restoreLife?: number;
  /** Warmth restored, 0..100. From `lv_warmth += X` in galaxy. */
  restoreWarmth?: number;
  /** Behavior to apply (e.g. "WellFedCutlet", "WellFedStrength").
   *  From `UnitBehaviorAdd(lp_unit, "X", ...)` in galaxy. */
  applyBehavior?: string;
  /** Duration in ms for the applied behavior. From CBehaviorBuff.Duration × 1000. */
  behaviorDurationMs?: number;
}

export interface ItemDef {
  id: ItemId;
  slot: ItemSlot;
  source: ItemSource;
  /** Player-friendly name from KTS GameStrings. */
  displayName: string;
  /** KTS markup verbatim — `<n/>`, `<c val="...">`, `<d ref="...">`. Client
   *  strips/renders later. */
  tooltip?: string;
  /** Normalized to number (default 0). KTS UINT_MAX / 1000 sentinels
   *  collapse to 999. Metadata for bot upgrade-scoring; not enforced at
   *  equip time per D14. */
  levelReq: number;
  stats: ItemStats;
  /** Crafting recipe ingredients. Empty object = not craftable (drop-only,
   *  starter, quest, boss, etc.). */
  craftingCost: ResourceCost;
  /** Crafting wind-up in seconds (KTS `CraftingData_Initialize` 3rd arg).
   *  Absent for non-craftable rows. */
  prepTimeSec?: number;
  /** Artisanship skill required before the recipe surfaces in the menu.
   *  Absent for non-craftable rows. */
  artisanshipReq?: number;
  /** Cooking skill (KTS units) required before a Campfire recipe surfaces.
   *  Mirrors `artisanshipReq` but gates on the Cooking tree. KTS gates cooking
   *  recipes at every 5 skill-levels (Cooking0/5/10/15/20 → v1 tiers 1–5);
   *  compared against `Cooking_tier × 5` in handleCraft. Absent/0 = no gate. */
  cookingReq?: number;
  // Item-level (non-stat) modifiers
  warmthDrainMult?: number;
  miningSpeedMult?: number;
  forestrySpeedMult?: number;
  petCapDelta?: number;
  /** Effect-system primitive names (e.g., "summon", "cooking-buff",
   *  "dot-tick"). Phase 3.5 dispatcher resolves these. */
  pendingEffects: ReadonlyArray<string>;
  /** Effect-system triggers. Empty for all v1 items
   *  (extraction's withTriggeredEffects: 0). Phase 3.5 wires this. */
  triggeredEffects: ReadonlyArray<string>;
  /** KTS behavior IDs the item grants while equipped. Read by
   *  `getActiveBehaviors(cube)` in derived-stats.ts so the formula branches
   *  (Bloodcleaver, RockbiterPickaxe, BerserkersBattleAxe, etc.) fire when
   *  the item is in an equipment slot. Most items list a single self-named
   *  behavior; some carry multiples (AbysmalClaw → AbysmalClaw + AntiHealSource).
   *  Empty array = item grants no behaviors (purely stat-only). */
  behaviorIds: ReadonlyArray<string>;
  /** Cooking-buff metadata for consumables — see `CookingBuffMeta`. Absent
   *  for non-consumables and consumables with no extracted side-effects. */
  cookingBuff?: CookingBuffMeta;
  /** Structured effect list from `ITEM_EFFECTS` (item-effects-data.ts).
   *  This is the NEW mechanism-based layer that runtime dispatchers consume.
   *  Populated by `normalizeItem` via `getItemEffects(def.id)`; defaults to
   *  `[]` for items with no entry in the table. Coexists with `pendingEffects`
   *  (the legacy bare-primitive tags, kept for `isUsable` only). */
  effects: ReadonlyArray<ItemEffect>;
  /** Left-click conversion: using this item consumes one and yields `count` of
   *  `itemId` (KTS "left-click a bear pelt → two leather"). The transform is
   *  self-contained — a convertible item carries no other use-effects. Drives
   *  both `isUsable` (so the client routes the click to `use`) and the server
   *  conversion in `items/use.ts`. Sourced as v1 design intent in `CONVERSIONS`
   *  below — KTS kept this in galaxy script, not item data, so it isn't in the
   *  extracted JSON (only the tooltip line is). */
  convertsTo?: { itemId: ItemId; count: number };
  /** Gold price when bought at a friendly Tavern (KTS `gt_Buy*` triggers —
   *  `CatalogFieldValueGet(c_gameCatalogEffect, "<Item>Cost", "Amount")`).
   *  Present ⇒ this item is purchasable at the Tavern spell-shop's Consumables
   *  section for `tavernPrice` gold (server `purchaseTavernItem` is the single
   *  validated buy path). Absent ⇒ not sold at the Tavern. v1 stock: Beer (6),
   *  Roasted Bison (5, item id RoastedLamb), Cleansing Draught (15) per docs/design/06-economy-gear.md
   *  §Tavern. (Raw-resource buyback + Cheese + Candle are CUT for v1.) */
  tavernPrice?: number;
}

// ---------------------------------------------------------------------------
// Loader: normalize the synthesizer JSON into typed ItemDef rows.
// ---------------------------------------------------------------------------

interface RawItem {
  id: string;
  slot: string;
  levelReq: string | number | null;
  displayName: string;
  tooltip: string | null;
  stats: Record<string, number | null>;
  source: string;
  craftingCost: Record<string, number> | null;
  prepTimeSec: number | null;
  artisanshipReq: number | null;
  cookingReq?: number | null;
  pendingEffects: string[] | null;
  triggeredEffects: string[] | null;
  behaviorIds: string[] | null;
  cookingBuff?: CookingBuffMeta | null;
  tavernPrice?: number | null;
  // Top-level item modifiers may live in `stats` in the JSON (synthesizer
  // co-located them there); we extract them in normalizeItem.
}

function normalizeLevelReq(raw: string | number | null | undefined): number {
  if (raw === null || raw === undefined) return 0;
  const n = typeof raw === "number" ? raw : parseInt(raw, 10);
  if (!Number.isFinite(n)) return 0;
  // KTS UINT_MAX sentinel + the "1000" placeholder both mean "never available".
  if (n >= 1000) return 999;
  return n;
}

const STAT_KEYS_SUB: ReadonlyArray<keyof ItemStats> = [
  "strength", "dexterity", "endurance", "intelligence", "willpower", "focus",
  "physique", "wit",
  "armor", "sArmor", "armorPen", "manaMax", "manaRegen", "hpRegen", "hpMax",
  "critChance", "critDamage",
  "attrBonusAnimal", "attrBonusTree", "attrBonusStructure", "attrBonusHero",
  "attackRange", "attackPeriod", "damagePoint", "moveSpeedMultiplier",
];

const ITEM_LEVEL_MODIFIERS = [
  "warmthDrainMult", "miningSpeedMult", "forestrySpeedMult", "petCapDelta",
] as const satisfies ReadonlyArray<keyof ItemDef>;

function normalizeStats(raw: Record<string, number | null> | null | undefined): ItemStats {
  const out: ItemStats = {};
  if (!raw) return out;
  for (const key of STAT_KEYS_SUB) {
    const v = raw[key as string];
    if (v !== null && v !== undefined) {
      // Cast: ItemStats fields are all `number | undefined`.
      (out as Record<string, number>)[key as string] = v;
    }
  }
  return out;
}

function normalizeItem(raw: RawItem): ItemDef {
  const stats = normalizeStats(raw.stats);

  const def: ItemDef = {
    id: raw.id,
    slot: raw.slot as ItemSlot,
    source: raw.source as ItemSource,
    displayName: raw.displayName,
    levelReq: normalizeLevelReq(raw.levelReq),
    stats,
    craftingCost: raw.craftingCost ?? {},
    pendingEffects: raw.pendingEffects ?? [],
    triggeredEffects: raw.triggeredEffects ?? [],
    behaviorIds: raw.behaviorIds ?? [],
    // Structured effects from the curated ITEM_EFFECTS table. The table is
    // the runtime source-of-truth; pendingEffects is kept for isUsable only.
    effects: getItemEffects(raw.id),
  };

  if (raw.tooltip != null) def.tooltip = raw.tooltip;
  if (raw.prepTimeSec != null) def.prepTimeSec = raw.prepTimeSec;
  if (raw.artisanshipReq != null) def.artisanshipReq = raw.artisanshipReq;
  if (raw.cookingReq != null) def.cookingReq = raw.cookingReq;
  if (raw.cookingBuff != null) def.cookingBuff = raw.cookingBuff;
  if (raw.tavernPrice != null) def.tavernPrice = raw.tavernPrice;

  // Item-level modifiers may be co-located in `stats` in the JSON; lift them
  // out so they're top-level on ItemDef per the type contract.
  for (const key of ITEM_LEVEL_MODIFIERS) {
    const v = raw.stats?.[key];
    if (v != null) (def[key] as number | undefined) = v;
  }

  return def;
}

interface ItemsJsonShape {
  _extraction_summary: unknown;
  items: Record<string, RawItem>;
}

function buildRegistry(json: ItemsJsonShape): Record<string, ItemDef> {
  const out: Record<string, ItemDef> = {};
  for (const [id, raw] of Object.entries(json.items)) {
    out[id] = normalizeItem(raw);
  }
  return out;
}

const allItems = buildRegistry(rawJson as unknown as ItemsJsonShape);

// ---------------------------------------------------------------------------
// Synthetic ItemDef rows — backpack-routed materials that lack a KTS extraction
// row. The RESOURCE_POLICY table (`resource-policy.ts`) maps the lowercase
// resource keys `fish`, `berry`, `herb` to PascalCase ItemIds; the registry
// must contain a matching ItemDef so addToBackpack / craft cost paths can
// resolve them. KTS never had item rows for these (gather drops directly into
// the per-cube counter without an inventory entry), so we synthesize bare-
// minimum ItemDefs here. If the synthesizer ever populates these, the JSON
// path wins (this loop guards with `if (!allItems[id])`).
// ---------------------------------------------------------------------------

const SYNTHETIC: Record<string, ItemDef> = {
  Fish:    { id: "Fish",    displayName: "Fish",    slot: "consumable", source: "RawResource", levelReq: 0, stats: {}, craftingCost: {}, triggeredEffects: [], pendingEffects: [], behaviorIds: [], effects: getItemEffects("Fish") },
  // Berry == KTS HandfulofBerries (the gathered raw-berry snack). KTS gf_EatFood
  // (MapScript:22904): heal = CheffingStats AttributeBonus[Light] = 30; hunger =
  // 30 × 0.5 = 15 (MapScript:22926). Edible by players AND bots via the cooking-
  // buff effect handler — this is the always-available survival snack.
  Berry:   { id: "Berry",   displayName: "Berry",   slot: "consumable", source: "RawResource", levelReq: 0, stats: {}, craftingCost: {}, triggeredEffects: [], pendingEffects: ["cooking-buff"], behaviorIds: [], cookingBuff: { restoreHunger: 15, restoreLife: 30 }, effects: getItemEffects("Berry") },
  Herb:    { id: "Herb",    displayName: "Herb",    slot: "consumable", source: "RawResource", levelReq: 0, stats: {}, craftingCost: {}, triggeredEffects: [], pendingEffects: [], behaviorIds: [], effects: getItemEffects("Herb") },
  // Fox is a mesh-reskin of KTS Pheasant (creatures.ts notes); KTS canon
  // drop is RawPheasant, our drop table writes RawFox for narrative parity.
  // Synthetic item row so the gather pickup fall-through resolves it.
  // RawFox is the Viking-reskin of KTS RawPheasant (Fox = Pheasant mesh swap);
  // it inherits RawPheasant's food value — heal 28, hunger 14 (CheffingStats
  // AttributeBonus[Mechanical]=28; hunger = ×0.5). Edible raw via AIFood.
  RawFox:  { id: "RawFox",  displayName: "Raw Fox Meat", slot: "consumable", source: "Drop", levelReq: 0, stats: {}, craftingCost: {}, triggeredEffects: [], pendingEffects: ["cooking-buff"], behaviorIds: [], cookingBuff: { restoreHunger: 14, restoreLife: 28 }, effects: getItemEffects("RawFox") },
};
for (const [id, def] of Object.entries(SYNTHETIC)) {
  if (!allItems[id]) allItems[id] = def;  // don't shadow if a future JSON regen adds them
}

// ---------------------------------------------------------------------------
// v1 item conversions. KTS implemented "left-click a bear pelt → two leather"
// in galaxy script, not item data, so it's absent from items-data.json (the
// tooltip line ships, but not the behavior). Re-attached here as design intent;
// patched onto the extracted rows so a future JSON regen can't drop it. Both the
// client click-dispatch (via isUsable) and server `items/use.ts` read this.
// ---------------------------------------------------------------------------
const CONVERSIONS: Record<string, { itemId: ItemId; count: number }> = {
  BearPelt: { itemId: "Leather", count: 2 },
};
for (const [id, convertsTo] of Object.entries(CONVERSIONS)) {
  const def = allItems[id];
  if (def) def.convertsTo = convertsTo;
}

/** The full KTS item catalog, normalized. Built once at module load. */
export const ITEMS: Readonly<Record<string, ItemDef>> = allItems;

/**
 * True if the item can be "used" via the use route (left-click in the backpack).
 *
 * KTS source data has `slot: "consumable"` on 79 items, but ~31 of those are
 * pure crafting materials (Iron Ore, Lumber, Stone, Wool, Leather, Antlers,
 * BearPelt, Shadowstone, RadiantGem, AncientArtifactFragment, …) — KTS used
 * "consumable" loosely to mean "lives in inventory and stacks", not "edible".
 * Without an effect-driven gate, every left-click on a material would silently
 * decrement the stack via `handleUse` (every primitive in `pendingEffects`
 * dispatches but EFFECT_HANDLERS is mostly empty in v1, so the visible result
 * is "the item disappeared and nothing happened").
 *
 * Gate: an item is usable iff it's in a usable slot AND has at least one
 * declared effect. Slot is the structural prerequisite (equipment items live
 * in the backpack but go through the `equip` route); effects are the semantic
 * one (no effect → no use action exists for this item even though KTS marked
 * it `consumable`).
 *
 * This predicate is consulted by:
 *   - server `items/use.ts:handleUse` — rejects materials at the wire layer
 *   - client `hud/main-plate.ts` — the click-dispatch picks `equip` vs `use`
 *     vs no-op based on this predicate (so click-on-IronOre is silently inert
 *     instead of misrouting to `use` and decrementing the stack)
 *
 * Future Phase 3.5 cooking-buff handler / effect-handler population will only
 * add to the set of usable items (handlers fire for items already flagged
 * with the matching primitive); no item that's a pure material today will
 * silently flip to "usable" later without being given an effect first.
 */
/**
 * Behavior ids carried by the CC-throw consumables (Task 6). Shared between
 * `isUsable` (gates the lower-charge variants as usable) and the server's
 * `items/use.ts` throw dispatch. The charge variants (e.g. DrunkenBooze2/3)
 * reuse the base behaviorId, so keying off behaviorId — not item id — covers
 * every variant with one entry.
 */
export const THROWABLE_CC_BEHAVIORS: ReadonlySet<string> = new Set([
  "DrunkenBooze",
  "DiseasedCocktail",
  "UnstableConcoction",
]);

/**
 * Resolve a CC-throw consumable's behavior family, or null if it isn't one.
 *
 * Prefers the item's `behaviorIds` (the data-driven source of truth), but falls
 * back to the item-id base with any trailing charge digit stripped
 * (DrunkenBooze3 → "DrunkenBooze"). The fallback is required because the KTS
 * extract left some lower-charge variants (DrunkenBooze2/3) with EMPTY
 * behaviorIds even though they are functionally the same thrown potion — the id
 * suffix is the only signal of the family on those rows. Both the `isUsable`
 * gate and the server throw-dispatch use this so every charge variant is
 * reachable in play regardless of the extract quirk.
 */
export function throwableCcBehaviorOf(def: ItemDef): string | null {
  for (const b of def.behaviorIds) {
    if (THROWABLE_CC_BEHAVIORS.has(b)) return b;
  }
  const base = def.id.replace(/[0-9]+$/, "");
  if (THROWABLE_CC_BEHAVIORS.has(base)) return base;
  return null;
}

/**
 * PowderedFlask family detection (Task 7 stealth). PowderedFlask is a utility
 * consumable whose use applies an AoE ally cloak (server: items/use.ts →
 * stealth-items.usePowderedFlask). Like the CC-throw potions, the lower-charge
 * variants (PowderedFlask2/3) carry EMPTY behaviorIds in the KTS extract, so we
 * fall back to the id-base (digit-stripped) to cover every charge. Used by both
 * `isUsable` (so the variants are reachable) and the server use-dispatch.
 */
export function isPowderedFlask(def: ItemDef): boolean {
  if (def.behaviorIds.includes("PowderedFlask")) return true;
  return def.id.replace(/[0-9]+$/, "") === "PowderedFlask";
}

export function isUsable(def: ItemDef): boolean {
  if (def.slot !== "consumable" && def.slot !== "utility") return false;
  if (def.convertsTo != null) return true;
  if (def.cookingBuff != null) return true;
  if (def.pendingEffects.length > 0) return true;
  if (def.triggeredEffects.length > 0) return true;
  // Task 7: PowderedFlask AoE ally-cloak (+ its empty-behaviorId charge variants).
  if (isPowderedFlask(def)) return true;
  // Task 6: CC-throw consumables (DrunkenBooze / DiseasedCocktail /
  // UnstableConcoction). Their charge variants (…2 / …3) carry empty
  // pendingEffects, and the throw mechanic is keyed off the throwable family in
  // items/use.ts. Without this gate the lower-charge variants would be
  // unreachable (handleUse rejects non-usable items). `throwableCcBehaviorOf`
  // covers the variants whose behaviorIds the KTS extract left empty.
  if (throwableCcBehaviorOf(def) !== null) return true;
  // Task 8: ReapersHand summon consumable — no pendingEffects in KTS extract,
  // but it IS a left-click consumable (slot: consumable, id: "ReapersHand").
  if (def.id === "ReapersHand") return true;
  return false;
}
